/**
 * DM translations are model output. The message bubble renders them with
 * `parseMessageContent`, which honors `<a href>` (the visible text can disagree
 * with the destination) and autolinks bare http(s) URLs. `detectDmSpam` only
 * scores the original `MessagePart.content`, so a translation must not be able
 * to introduce a clickable destination the sender never wrote.
 *
 * Comment and post translation keep their own HTML repair. This is DM-only.
 */

const HTML_TAG_RE = /<\/?[a-zA-Z][a-zA-Z0-9]*\b[^>]*>/g;
const BR_TAG_RE = /<br\b[^>]*>/gi;
const HTTP_URL_RE = /https?:\/\/[^\s<>"']+/gi;
const TRAILING_PUNCT_RE = /[.,!;:]+$/;
const HREF_ATTR_RE = /\bhref\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s"'`=<>]+))/gi;

function trimTrailingUrlPunctuation(url: string): string {
  return url.replace(TRAILING_PUNCT_RE, '').trim();
}

/**
 * Same gate `parseMessageContent` / `linkifyPlainUrls` use before turning a
 * match into an `<a href>`. A string that fails here is not a clickable link.
 */
function isLinkableHttpUrl(url: string): boolean {
  if (!url || typeof url !== 'string') return false;
  const trimmed = url.trim();
  if (!trimmed) return false;

  try {
    const urlObj = new URL(trimmed);
    if (urlObj.protocol !== 'http:' && urlObj.protocol !== 'https:') return false;
    if (!urlObj.hostname || urlObj.hostname.length === 0) return false;

    const hasValidHostname =
      urlObj.hostname === 'localhost' || urlObj.hostname.includes('.') || /^\[.*\]$/.test(urlObj.hostname);
    if (!hasValidHostname) return false;

    const beforeQuery = trimmed.split('?')[0] ?? trimmed;
    const beforeHash = beforeQuery.split('#')[0] ?? beforeQuery;
    if (beforeHash.includes(';')) return false;

    return true;
  } catch {
    return false;
  }
}

/** Browser destination for a match the message renderer would autolink, or null. */
function linkDestination(raw: string): string | null {
  const trimmed = trimTrailingUrlPunctuation(raw);
  if (!isLinkableHttpUrl(trimmed)) return null;
  try {
    return new URL(trimmed).href;
  } catch {
    return null;
  }
}

function addDestination(destinations: Set<string>, raw: string | undefined): void {
  if (!raw) return;
  const destination = linkDestination(raw);
  if (destination) destinations.add(destination);
}

function collectSourceDestinations(source: string): Set<string> {
  const destinations = new Set<string>();

  for (const match of source.matchAll(HREF_ATTR_RE)) {
    addDestination(destinations, match[1] ?? match[2] ?? match[3]);
  }

  const withoutTags = source.replace(BR_TAG_RE, '\n').replace(HTML_TAG_RE, ' ');
  for (const match of withoutTags.matchAll(HTTP_URL_RE)) {
    addDestination(destinations, match[0]);
  }

  return destinations;
}

function stripTags(value: string): string {
  return value.replace(BR_TAG_RE, '\n').replace(HTML_TAG_RE, '');
}

/**
 * Strip tags from a DM translation and drop any http(s) URL the bubble would
 * link whose destination is not already in the source message.
 *
 * Tags come off first. `HTTP_URL_RE` stops at `<`, so a tag splitting a URL is
 * not a match; stripping afterward would join the fragments into a destination
 * the sender never wrote.
 *
 * An allowed URL is kept with the translation's own spelling, so the visible
 * text and the href are the same string. A novel bare URL is removed. An
 * injected `<a>` cannot survive: its tags are removed, and an href the source
 * never contained is removed with them.
 */
export function pinDmTranslationLinks(
  translated: string | null | undefined,
  source: string | null | undefined
): string {
  if (translated == null) return '';

  const allowed = collectSourceDestinations(source ?? '');
  return stripTags(translated).replace(HTTP_URL_RE, (raw) => {
    const destination = linkDestination(raw);
    if (!destination) return raw;
    return allowed.has(destination) ? raw : '';
  });
}
